import { getI18n } from '@i18n';
import { AppRoutes } from '@types';
import {
  BarChart3,
  BookOpen,
  Briefcase,
  ClipboardCheck,
  Cpu,
  Database,
  FileSearch,
  HelpCircle,
  LayoutDashboard,
  Layers,
  List,
  ListChecks,
  Settings,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import React from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const t = (key: any) => getI18n().t(key) as string;

export interface SidebarEntry {
  lucideIcon?: LucideIcon;
  icon?: React.ReactNode;
  id?: string;
  label: string;
  href?: AppRoutes | string | null;
  upcoming?: boolean;
  onClick?: () => void;
  subitems?: SidebarEntry[];
  subtitle?: boolean;
  activeForPrefixes?: string[];
  isGroup?: boolean;
  isSubGroup?: boolean;
  defaultOpen?: boolean;
  testId?: string;
  external?: boolean;
}

export const getSidebarTopLinks = (): SidebarEntry[] => [
  {
    label: 'Dashboard',
    href: AppRoutes.dashboard,
    lucideIcon: LayoutDashboard,
  },
  {
    label: 'Data Management',
    lucideIcon: Layers,
    isGroup: true,
    defaultOpen: true,
    subitems: [
      {
        label: t('usecase:sidebar.inventory'),
        href: AppRoutes.usecasesInventory,
        lucideIcon: Briefcase,
        activeForPrefixes: [AppRoutes.useCaseAdd, AppRoutes.useCaseUpdate, AppRoutes.useCaseDetails],
        testId: 'sidebar-usecase-inventory',
      },
      {
        label: 'Model Inventory',
        href: AppRoutes.modelsInventory,
        lucideIcon: Cpu,
        // model detail/create/update pages are under /inventory/models/
        activeForPrefixes: [AppRoutes.modelDetails, AppRoutes.aiModelAdd, AppRoutes.aiModelUpdate],
        testId: 'sidebar-model-inventory',
      },
      {
        label: t('dataset:sidebar.inventory'),
        href: AppRoutes.datasetInventory,
        lucideIcon: Database,
        testId: 'sidebar-dataset-inventory',
      },
    ],
  },
  {
    label: 'Lifecycles',
    lucideIcon: Workflow,
    href: AppRoutes.lifecycle,
  },
  {
    label: 'Assessments',
    lucideIcon: ClipboardCheck,
    isGroup: true,
    defaultOpen: true,
    subitems: [
      {
        label: 'Model Assessment',
        href: AppRoutes.dataCollection,
        lucideIcon: FileSearch,
      },
      {
        id: 'governance-evaluation',
        label: t('customAssessment:sidebar.label'),
        isSubGroup: true,
        defaultOpen: false,
        subitems: [
          // Dynamic custom questionnaires are appended here at runtime
        ],
      },
    ],
  },
  {
    label: 'Results',
    lucideIcon: BarChart3,
    isGroup: true,
    defaultOpen: true,
    activeForPrefixes: [AppRoutes.assessmentCompare],
    subitems: [
      {
        label: 'Model Results',
        href: AppRoutes.assessmentInventory,
        lucideIcon: ListChecks,
        activeForPrefixes: [AppRoutes.assessmentCompare],
      },
      {
        label: t('customAssessment:sidebar.logsLabel'),
        href: AppRoutes.governanceEvaluationInventory,
        lucideIcon: List,
      },
    ],
  },
  {
    label: 'Documentation',
    href: AppRoutes.docs,
    lucideIcon: BookOpen,
    external: true,
  },
];

export const getSidebarBottomLinks = (): SidebarEntry[] => [
  {
    label: 'Quick Start',
    lucideIcon: HelpCircle,
  },
  {
    label: 'Settings',
    href: AppRoutes.settings,
    lucideIcon: Settings,
  },
];
